import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CacheKeyService } from '../cache-key.service';
import { DBService } from '../db.service';
import apiPaths from 'constants/api-paths';
import { appDb } from 'dexie/app.db';
import { generateURL } from 'helpers/api-path.helper';
import {
  switchMap,
  from,
  catchError,
  of,
  Observable,
  map,
  throwError,
  combineLatest,
} from 'rxjs';
import { WarehouseApiService } from './warehouse-api.service';
import { CompanyApiService } from './firms-api.service';
import { WarehouseReportApiService } from './warehouse-report-api.service';

@Injectable({
  providedIn: 'root',
})
export class WarehouseReportErrorApiService {
  protected dbService = inject(DBService);
  protected cacheKeyService = inject(CacheKeyService);
  protected http = inject(HttpClient);
  protected warehouseReportApiService = inject(WarehouseReportApiService);
  protected warehouseApiService = inject(WarehouseApiService);
  protected companyApiService = inject(CompanyApiService);

  services = {
    companyKey: (c) => this.companyApiService.getFirms(c),
    wareHouseKey: (c) => this.warehouseApiService.getWareHouseList(c),
    wareHouseReportKey: (c) => this.warehouseReportApiService.getList(c),
  };

  getList(cacheKey?: string) {
    return this.http
      .get(
        generateURL(
          apiPaths.warehouseReportErrorList,
          {},
          new HttpParams({ fromObject: { cacheKey } })
        )
      )
      .pipe(
        switchMap((data: any) => {
          if (data.isAllDb) {
            return from(
              appDb.warehouseReportError.clear().then(() =>
                appDb.warehouseReportError.bulkAdd(
                  data.items.map((item) => ({
                    ...item,
                    createDate: item.createDate || item.createdAt,
                    isRepaired: item.isRepaired ? 1 : 0,
                    isSynced: 1,
                    isDeleted: item.isDeleted ? 1 : 0,
                  }))
                )
              )
            );
          }
          return from(
            appDb.warehouseReportError.bulkPut(
              data.items.map((item) => ({
                ...item,
                createDate: item.createDate || item.createdAt,
                isRepaired: item.isRepaired ? 1 : 0,
                isSynced: 1,
                isDeleted: item.isDeleted ? 1 : 0,
              }))
            )
          );
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  create(data): Observable<any> {
    return this.http
      .post(generateURL(apiPaths.warehouseReportError), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  update(id, data) {
    return this.http
      .put(generateURL(apiPaths.warehouseReportErrorId, { id }), data)
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
  delete(id) {
    return this.http
      .delete(generateURL(apiPaths.warehouseReportErrorId, { id }))
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  move(wareHouseReportErrorId, fromWareHouseReportId, toWareHouseReportId) {
    return this.http
      .put(generateURL(apiPaths.warehouseReportErrorMove), {
        wareHouseReportErrorId,
        fromWareHouseReportId,
        toWareHouseReportId,
      })
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }

  bulk(reportErrors) {
    return this.http.post(
      generateURL(apiPaths.warehouseReportErrorBulk),
      reportErrors.map((item) => ({
        ...item,
        isDeleted: item.isDeleted ? true : false,
      }))
    );
  }

  changeRepairedStatus(wareHouseReportErrorId, isRepaired) {
    return this.http
      .put(generateURL(apiPaths.warehouseReportErrorRepairedStatus), {
        wareHouseReportErrorId,
        isRepaired,
      })
      .pipe(
        switchMap((res) =>
          this.dbService.getRow(appDb.cacheKeys, 'wareHouseReportError').pipe(
            switchMap((cache) => {
              return this.getList(cache.hash).pipe(map(() => res));
            })
          )
        ),
        switchMap((res) =>
          this.cacheKeyService
            .checkAndUpdateTables(undefined, this.services)
            .pipe(map(() => res))
        ),
        catchError((err) => throwError(() => err))
      );
  }
}
